import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Card from '../common/Card';
import Button from '../common/Button';
import Input from '../common/Input';
import api from '../../services/api';
import {
  HOMEPAGE_DEFAULTS,
  deepMergeHomepage,
  resolveMediaUrl,
  toEmbedUrl,
  isDirectVideoFile,
  getDemoVideoUrl
} from '../../constants/homepageDefaults';
import '../../styles/admin.css';

const TABS = [
  { id: 'brand', label: 'Brand & Logo' },
  { id: 'hero', label: 'Hero' },
  { id: 'trust', label: 'Trust Logos' },
  { id: 'problems', label: 'Pain Points' },
  { id: 'demo', label: 'Demo' },
  { id: 'capabilities', label: 'Capabilities' },
  { id: 'industries', label: 'Industries' },
  { id: 'howItWorks', label: 'How It Works' },
  { id: 'localization', label: 'Localization' },
  { id: 'caseStudy', label: 'Case Study' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'faq', label: 'FAQ' },
  { id: 'cta', label: 'Bottom CTA' },
  { id: 'footer', label: 'Footer' }
];

const HomepageManager = () => {
  const [tab, setTab] = useState('brand');
  const [content, setContent] = useState(deepMergeHomepage(HOMEPAGE_DEFAULTS));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [heroImageFile, setHeroImageFile] = useState(null);
  const [heroImagePreview, setHeroImagePreview] = useState('');

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    setLoading(true);
    try {
      const res = await api.get('/site-content.php');
      const merged = deepMergeHomepage(res.data.content || {});
      setContent(merged);
      setLogoPreview(resolveMediaUrl(merged.brand?.logoUrl));
      setHeroImagePreview(resolveMediaUrl(merged.hero?.imageUrl));
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Failed to load homepage content');
      setContent(deepMergeHomepage(HOMEPAGE_DEFAULTS));
    } finally {
      setLoading(false);
    }
  };

  const setSection = (section, patch) => {
    setContent((prev) => ({
      ...prev,
      [section]: { ...prev[section], ...patch }
    }));
  };

  const updateListItem = (section, listKey, index, patch) => {
    setContent((prev) => {
      const list = [...(prev[section][listKey] || [])];
      list[index] = { ...list[index], ...patch };
      return { ...prev, [section]: { ...prev[section], [listKey]: list } };
    });
  };

  const updateStringListItem = (section, listKey, index, value) => {
    setContent((prev) => {
      const list = [...(prev[section][listKey] || [])];
      list[index] = value;
      return { ...prev, [section]: { ...prev[section], [listKey]: list } };
    });
  };

  const addListItem = (section, listKey, blank) => {
    setContent((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [listKey]: [...(prev[section][listKey] || []), blank]
      }
    }));
  };

  const removeListItem = (section, listKey, index) => {
    setContent((prev) => {
      const list = [...(prev[section][listKey] || [])];
      list.splice(index, 1);
      return { ...prev, [section]: { ...prev[section], [listKey]: list } };
    });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleHeroImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setHeroImageFile(file);
    setHeroImagePreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (logoFile || heroImageFile) {
        const form = new FormData();
        if (logoFile) form.append('site_logo', logoFile);
        if (heroImageFile) form.append('hero_image', heroImageFile);
        form.append('content', JSON.stringify(content));
        
        const res = await api.post('/site-content.php', form, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const merged = deepMergeHomepage(res.data.content || content);
        setContent(merged);
        setLogoPreview(resolveMediaUrl(merged.brand?.logoUrl));
        setLogoFile(null);
        setHeroImagePreview(resolveMediaUrl(merged.hero?.imageUrl));
        setHeroImageFile(null);
      } else {
        const res = await api.put('/site-content.php', content);
        setContent(deepMergeHomepage(res.data.content || content));
      }
      toast.success('Homepage content saved');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = () => {
    if (!window.confirm('Reset all homepage content to Harvyq defaults?')) return;
    setContent(deepMergeHomepage(HOMEPAGE_DEFAULTS));
    setLogoFile(null);
    setLogoPreview('');
    setHeroImageFile(null);
    setHeroImagePreview('');
    toast('Defaults loaded — click Save to apply');
  };

  if (loading) {
    return (
      <div className="admin-page">
        <Card>
          <p>Loading homepage content…</p>
        </Card>
      </div>
    );
  }

  const { brand, hero, trust, problems, demo, capabilities, industries, howItWorks, localization, caseStudy, pricing, faq, cta, footer } = content;

  return (
    <div className="admin-page homepage-manager">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h1>Homepage Content</h1>
          <p className="page-subtitle">
            Manage your SaaS landing page content
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <Button variant="secondary" onClick={handleResetDefaults}>
            Reset Defaults
          </Button>
          <Button variant="primary" loading={saving} onClick={handleSave}>
            Save Changes
          </Button>
        </div>
      </div>

      <div className="homepage-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`homepage-tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'brand' && (
        <Card title="Brand & Logo">
          <div className="form-grid">
            <Input label="Product name" name="productName" value={brand?.productName} onChange={(e) => setSection('brand', { productName: e.target.value })} />
            <Input label="Parent company" name="parentCompany" value={brand?.parentCompany} onChange={(e) => setSection('brand', { parentCompany: e.target.value })} />
            <Input label="Tagline" name="tagline" value={brand?.tagline} onChange={(e) => setSection('brand', { tagline: e.target.value })} />
            <Input label="Logo alt text" name="logoAlt" value={brand?.logoAlt} onChange={(e) => setSection('brand', { logoAlt: e.target.value })} />
          </div>
          <div className="logo-upload-block">
            <label className="input-label">Site logo</label>
            <div className="logo-upload-row">
              {logoPreview ? (
                <img src={logoPreview} alt="Logo preview" className="logo-preview" />
              ) : (
                <div className="logo-preview placeholder">No logo</div>
              )}
              <div>
                <input type="file" accept="image/*,.svg" onChange={handleLogoChange} />
                <p className="hint">PNG, JPG, WebP or SVG — max 5MB. Saved on Save Changes.</p>
                {brand?.logoUrl && (
                  <Input label="Logo URL (stored)" name="logoUrl" value={brand.logoUrl} onChange={(e) => {
                      setSection('brand', { logoUrl: e.target.value });
                      setLogoPreview(resolveMediaUrl(e.target.value));
                    }} />
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {tab === 'hero' && (
        <Card title="Hero section">
          <Input label="Badge Text" name="badge" value={hero?.badge} onChange={(e) => setSection('hero', { badge: e.target.value })} />
          <Input label="Headline" name="headline" value={hero?.headline} onChange={(e) => setSection('hero', { headline: e.target.value })} />
          <Input type="textarea" label="Subheadline" name="subheadline" rows={3} value={hero?.subheadline} onChange={(e) => setSection('hero', { subheadline: e.target.value })} />
          <div className="form-grid">
            <Input label="Primary CTA label" name="primaryCta" value={hero?.primaryCta} onChange={(e) => setSection('hero', { primaryCta: e.target.value })} />
            <Input label="Primary CTA link" name="primaryCtaLink" value={hero?.primaryCtaLink} onChange={(e) => setSection('hero', { primaryCtaLink: e.target.value })} />
            <Input label="Secondary CTA label" name="secondaryCta" value={hero?.secondaryCta} onChange={(e) => setSection('hero', { secondaryCta: e.target.value })} />
            <Input label="Secondary CTA link" name="secondaryCtaLink" value={hero?.secondaryCtaLink} onChange={(e) => setSection('hero', { secondaryCtaLink: e.target.value })} />
          </div>
          <Input label="Microcopy (under buttons)" name="microcopy" value={hero?.microcopy} onChange={(e) => setSection('hero', { microcopy: e.target.value })} />
          <div className="logo-upload-block mt-4">
            <label className="input-label">Hero Image</label>
            <div className="logo-upload-row">
              {heroImagePreview ? (
                <img src={heroImagePreview} alt="Hero preview" className="logo-preview" style={{width: 'auto', maxWidth: '200px'}} />
              ) : (
                <div className="logo-preview placeholder">No image</div>
              )}
              <div>
                <input type="file" accept="image/*" onChange={handleHeroImageChange} />
                <p className="hint">PNG, JPG, WebP — max 5MB. Saved on Save Changes.</p>
                {hero?.imageUrl && (
                  <Input label="Image URL (stored)" name="imageUrl" value={hero.imageUrl} onChange={(e) => {
                      setSection('hero', { imageUrl: e.target.value });
                      setHeroImagePreview(resolveMediaUrl(e.target.value));
                    }} />
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {tab === 'trust' && (
        <Card title="Trust Logos">
          <Input label="Headline" name="trustHeadline" value={trust?.headline} onChange={(e) => setSection('trust', { headline: e.target.value })} />
          <h3>Logos (Text placeholders)</h3>
          {(trust?.logos || []).map((logo, i) => (
            <div key={i} className="list-row form-grid">
              <Input label={`Logo ${i + 1}`} name={`logo-${i}`} value={logo} onChange={(e) => updateStringListItem('trust', 'logos', i, e.target.value)} />
              <Button variant="danger" size="sm" onClick={() => removeListItem('trust', 'logos', i)}>Remove</Button>
            </div>
          ))}
          <Button variant="secondary" size="sm" onClick={() => addListItem('trust', 'logos', '')}>Add Logo</Button>
        </Card>
      )}

      {tab === 'problems' && (
        <Card title="Pain Points">
          <Input label="Section Title" name="problemsTitle" value={problems?.sectionTitle} onChange={(e) => setSection('problems', { sectionTitle: e.target.value })} />
          <Input label="Solution Transition Title" name="problemsTransition" value={problems?.solutionTransitionTitle} onChange={(e) => setSection('problems', { solutionTransitionTitle: e.target.value })} />
          <h3>Problem Items</h3>
          {(problems?.items || []).map((item, i) => (
            <div key={i} className="list-row form-grid">
              <Input label={`Problem ${i + 1}`} name={`problem-${i}`} value={item.text} onChange={(e) => updateListItem('problems', 'items', i, { text: e.target.value })} />
              <Button variant="danger" size="sm" onClick={() => removeListItem('problems', 'items', i)}>Remove</Button>
            </div>
          ))}
          <Button variant="secondary" size="sm" onClick={() => addListItem('problems', 'items', { text: '' })}>Add Problem</Button>
        </Card>
      )}

      {tab === 'demo' && (
        <Card title="Demo Section">
          <label className="checkbox-row">
            <input type="checkbox" checked={!!demo?.enabled} onChange={(e) => setSection('demo', { enabled: e.target.checked })} />
            Show demo section on homepage
          </label>
          <Input label="Section Title" name="demoTitle" value={demo?.sectionTitle} onChange={(e) => setSection('demo', { sectionTitle: e.target.value })} />
          <Input label="Section Subtitle" name="demoSubtitle" value={demo?.sectionSubtitle} onChange={(e) => setSection('demo', { sectionSubtitle: e.target.value })} />
          <Input label="Video URL (YouTube, Vimeo, etc.)" name="demoUrl" value={getDemoVideoUrl(demo)} onChange={(e) => setSection('demo', { videoUrl: e.target.value })} />
          <p className="hint">
            Paste a normal share link (e.g. https://youtu.be/… or https://vimeo.com/…), Google Drive file link, or a direct .mp4 URL.
            Hero / Bottom CTA buttons with link <code>#demo</code> will play this video.
          </p>
          {getDemoVideoUrl(demo) && (
            <div style={{ maxWidth: 480, aspectRatio: '16/9', marginTop: '0.75rem', background: '#000', borderRadius: 8, overflow: 'hidden' }}>
              {isDirectVideoFile(getDemoVideoUrl(demo)) ? (
                <video src={getDemoVideoUrl(demo)} controls style={{ width: '100%', height: '100%' }} />
              ) : (
                <iframe
                  src={toEmbedUrl(getDemoVideoUrl(demo))}
                  title="Demo video preview"
                  style={{ width: '100%', height: '100%', border: 0 }}
                  allow="encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              )}
            </div>
          )}
        </Card>
      )}

      {tab === 'capabilities' && (
        <Card title="Capabilities">
          <Input label="Section Title" name="capTitle" value={capabilities?.sectionTitle} onChange={(e) => setSection('capabilities', { sectionTitle: e.target.value })} />
          {(capabilities?.items || []).map((item, i) => (
            <div key={i} className="nested-card">
              <div className="nested-card-header">
                <h4>Capability {i + 1}</h4>
                <Button variant="danger" size="sm" onClick={() => removeListItem('capabilities', 'items', i)}>Remove</Button>
              </div>
              <div className="form-grid">
                <Input label="Icon (emoji)" name={`cap-icon-${i}`} value={item.icon} onChange={(e) => updateListItem('capabilities', 'items', i, { icon: e.target.value })} />
                <Input label="Title" name={`cap-title-${i}`} value={item.title} onChange={(e) => updateListItem('capabilities', 'items', i, { title: e.target.value })} />
              </div>
              <Input type="textarea" label="Description" name={`cap-desc-${i}`} rows={2} value={item.description} onChange={(e) => updateListItem('capabilities', 'items', i, { description: e.target.value })} />
            </div>
          ))}
          <Button variant="secondary" size="sm" onClick={() => addListItem('capabilities', 'items', { icon: '✨', title: '', description: '' })}>Add Capability</Button>
        </Card>
      )}

      {tab === 'industries' && (
        <Card title="Industries">
          <Input label="Section Title" name="indTitle" value={industries?.sectionTitle} onChange={(e) => setSection('industries', { sectionTitle: e.target.value })} />
          {(industries?.items || []).map((item, i) => (
            <div key={i} className="nested-card">
              <div className="nested-card-header">
                <h4>Industry {i + 1}</h4>
                <Button variant="danger" size="sm" onClick={() => removeListItem('industries', 'items', i)}>Remove</Button>
              </div>
              <div className="form-grid">
                <Input label="Icon (emoji)" name={`ind-icon-${i}`} value={item.icon} onChange={(e) => updateListItem('industries', 'items', i, { icon: e.target.value })} />
                <Input label="Title" name={`ind-title-${i}`} value={item.title} onChange={(e) => updateListItem('industries', 'items', i, { title: e.target.value })} />
                <Input label="Link" name={`ind-link-${i}`} value={item.link} onChange={(e) => updateListItem('industries', 'items', i, { link: e.target.value })} />
              </div>
              <label className="checkbox-row mt-2">
                <input type="checkbox" checked={!!item.highlight} onChange={(e) => updateListItem('industries', 'items', i, { highlight: e.target.checked })} />
                Highlight this card (e.g., for "Other")
              </label>
            </div>
          ))}
          <Button variant="secondary" size="sm" onClick={() => addListItem('industries', 'items', { icon: '', title: '', link: '', highlight: false })}>Add Industry</Button>
        </Card>
      )}

      {tab === 'howItWorks' && (
        <Card title="How It Works">
          <Input label="Section Title" name="howTitle" value={howItWorks?.sectionTitle} onChange={(e) => setSection('howItWorks', { sectionTitle: e.target.value })} />
          {(howItWorks?.steps || []).map((step, i) => (
            <div key={i} className="nested-card">
              <div className="nested-card-header">
                <h4>Step {i + 1}</h4>
                <Button variant="danger" size="sm" onClick={() => removeListItem('howItWorks', 'steps', i)}>Remove</Button>
              </div>
              <div className="form-grid">
                <Input label="Number" name={`how-num-${i}`} value={step.number} onChange={(e) => updateListItem('howItWorks', 'steps', i, { number: e.target.value })} />
                <Input label="Title" name={`how-title-${i}`} value={step.title} onChange={(e) => updateListItem('howItWorks', 'steps', i, { title: e.target.value })} />
              </div>
              <Input type="textarea" label="Description" name={`how-desc-${i}`} rows={2} value={step.description} onChange={(e) => updateListItem('howItWorks', 'steps', i, { description: e.target.value })} />
            </div>
          ))}
          <Button variant="secondary" size="sm" onClick={() => addListItem('howItWorks', 'steps', { number: '', title: '', description: '' })}>Add Step</Button>
        </Card>
      )}

      {tab === 'localization' && (
        <Card title="Localization">
          <Input label="Section Title" name="locTitle" value={localization?.sectionTitle} onChange={(e) => setSection('localization', { sectionTitle: e.target.value })} />
          <h3>Features</h3>
          {(localization?.features || []).map((feature, i) => (
            <div key={i} className="list-row form-grid">
              <Input label={`Feature ${i + 1}`} name={`loc-${i}`} value={feature} onChange={(e) => updateStringListItem('localization', 'features', i, e.target.value)} />
              <Button variant="danger" size="sm" onClick={() => removeListItem('localization', 'features', i)}>Remove</Button>
            </div>
          ))}
          <Button variant="secondary" size="sm" onClick={() => addListItem('localization', 'features', '')}>Add Feature</Button>
        </Card>
      )}

      {tab === 'caseStudy' && (
        <Card title="Case Study / Featured Review">
          <Input type="textarea" label="Quote" name="caseQuote" rows={3} value={caseStudy?.quote} onChange={(e) => setSection('caseStudy', { quote: e.target.value })} />
          <div className="form-grid">
            <Input label="Author Name" name="caseName" value={caseStudy?.authorName} onChange={(e) => setSection('caseStudy', { authorName: e.target.value })} />
            <Input label="Author Role/Business" name="caseRole" value={caseStudy?.authorRole} onChange={(e) => setSection('caseStudy', { authorRole: e.target.value })} />
          </div>
        </Card>
      )}

      {tab === 'pricing' && (
        <Card title="Pricing cards">
          <Input label="Section title" name="priceTitle" value={pricing?.sectionTitle} onChange={(e) => setSection('pricing', { sectionTitle: e.target.value })} />
          {(pricing?.plans || []).map((plan, i) => (
            <div key={i} className="nested-card">
              <div className="nested-card-header">
                <h4>{plan.name || `Plan ${i + 1}`}</h4>
                <Button variant="danger" size="sm" onClick={() => removeListItem('pricing', 'plans', i)}>Remove</Button>
              </div>
              <div className="form-grid">
                <Input label="Name" name={`plan-name-${i}`} value={plan.name} onChange={(e) => updateListItem('pricing', 'plans', i, { name: e.target.value })} />
                <Input label="Description" name={`plan-desc-${i}`} value={plan.desc} onChange={(e) => updateListItem('pricing', 'plans', i, { desc: e.target.value })} />
                <Input label="Price (Monthly)" name={`plan-pm-${i}`} value={plan.priceMonthly} onChange={(e) => updateListItem('pricing', 'plans', i, { priceMonthly: e.target.value })} />
                <Input label="Price (Annually)" name={`plan-pa-${i}`} value={plan.priceAnnually} onChange={(e) => updateListItem('pricing', 'plans', i, { priceAnnually: e.target.value })} />
                <Input label="CTA Text" name={`plan-cta-${i}`} value={plan.cta} onChange={(e) => updateListItem('pricing', 'plans', i, { cta: e.target.value })} />
              </div>
              <label className="checkbox-row mt-2">
                <input type="checkbox" checked={!!plan.popular} onChange={(e) => updateListItem('pricing', 'plans', i, { popular: e.target.checked })} />
                Mark as most popular
              </label>
              <Input
                type="textarea"
                label="Features (one per line)"
                name={`plan-features-${i}`}
                rows={5}
                value={(plan.features || []).join('\n')}
                onChange={(e) => updateListItem('pricing', 'plans', i, { features: e.target.value.split('\n').map((l) => l.trim()).filter(Boolean) })}
              />
            </div>
          ))}
          <Button variant="secondary" size="sm" onClick={() => addListItem('pricing', 'plans', { name: 'New Plan', desc: '', priceMonthly: '0', priceAnnually: '0', cta: 'Start', features: [], popular: false })}>Add plan</Button>
        </Card>
      )}

      {tab === 'faq' && (
        <Card title="FAQ">
          <Input label="Section Title" name="faqTitle" value={faq?.sectionTitle} onChange={(e) => setSection('faq', { sectionTitle: e.target.value })} />
          {(faq?.items || []).map((item, i) => (
            <div key={i} className="nested-card">
              <div className="nested-card-header">
                <h4>FAQ {i + 1}</h4>
                <Button variant="danger" size="sm" onClick={() => removeListItem('faq', 'items', i)}>Remove</Button>
              </div>
              <Input label="Question" name={`faq-q-${i}`} value={item.q} onChange={(e) => updateListItem('faq', 'items', i, { q: e.target.value })} />
              <Input type="textarea" label="Answer" name={`faq-a-${i}`} rows={3} value={item.a} onChange={(e) => updateListItem('faq', 'items', i, { a: e.target.value })} />
            </div>
          ))}
          <Button variant="secondary" size="sm" onClick={() => addListItem('faq', 'items', { q: '', a: '' })}>Add FAQ</Button>
        </Card>
      )}

      {tab === 'cta' && (
        <Card title="Bottom CTA">
          <Input label="Headline" name="ctaHeadline" value={cta?.headline} onChange={(e) => setSection('cta', { headline: e.target.value })} />
          <Input type="textarea" label="Subtext" name="ctaSubtext" rows={2} value={cta?.subtext} onChange={(e) => setSection('cta', { subtext: e.target.value })} />
          <div className="form-grid">
            <Input label="Primary CTA label" name="ctaPri" value={cta?.primaryCta} onChange={(e) => setSection('cta', { primaryCta: e.target.value })} />
            <Input label="Primary CTA link" name="ctaPriL" value={cta?.primaryCtaLink} onChange={(e) => setSection('cta', { primaryCtaLink: e.target.value })} />
            <Input label="Secondary CTA label" name="ctaSec" value={cta?.secondaryCta} onChange={(e) => setSection('cta', { secondaryCta: e.target.value })} />
            <Input label="Secondary CTA link" name="ctaSecL" value={cta?.secondaryCtaLink} onChange={(e) => setSection('cta', { secondaryCtaLink: e.target.value })} />
          </div>
          <Input label="Microcopy" name="ctaMicro" value={cta?.microcopy} onChange={(e) => setSection('cta', { microcopy: e.target.value })} />
        </Card>
      )}

      {tab === 'footer' && (
        <Card title="Footer">
          <Input type="textarea" label="About text" name="footerAbout" rows={3} value={footer?.about} onChange={(e) => setSection('footer', { about: e.target.value })} />
          <div className="form-grid">
            <Input label="Phone" name="footerPhone" value={footer?.phone} onChange={(e) => setSection('footer', { phone: e.target.value })} />
            <Input label="Email" name="footerEmail" value={footer?.email} onChange={(e) => setSection('footer', { email: e.target.value })} />
            <Input label="Address" name="footerAddress" value={footer?.address} onChange={(e) => setSection('footer', { address: e.target.value })} />
            <Input label="Copyright line" name="footerCopyright" value={footer?.copyright} onChange={(e) => setSection('footer', { copyright: e.target.value })} />
          </div>
          <h3>Social links</h3>
          <div className="form-grid">
            {['facebook', 'twitter', 'instagram', 'linkedin'].map((key) => (
              <Input key={key} label={key.charAt(0).toUpperCase() + key.slice(1)} name={`social-${key}`} value={footer?.social?.[key] || ''} onChange={(e) => setSection('footer', { social: { ...footer?.social, [key]: e.target.value } })} />
            ))}
          </div>
        </Card>
      )}

      <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="primary" loading={saving} onClick={handleSave}>
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default HomepageManager;
